import { useCallback } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import invariant from 'tiny-invariant'
import { TextFieldInput, GridItem } from '@sequencehq/core-components'
import { lineItemGroupsAtom } from 'Invoices/atoms/invoiceEditing.atom'
import {
  Controls,
  FieldGrid,
  FieldLabel,
  SecondaryOptions,
  Title
} from '../../common/layout'
import { BaseConfig, EditLineItemConfig } from '../../types'
import {
  DescriptionField,
  LedgerIntegrationFields,
  PercentageRatesToggle,
  QuantityField,
  RateField
} from '../../common/fields'
import {
  descriptionAtom,
  descriptionEnabledAtom,
  externalIdsAtom,
  formStateAtom,
  quantityAtom,
  rateAtom,
  rateDisplayAtom,
  titleAtom
} from './state.atoms'
import { validateFormAtom, validationResultsAtom } from './validation.atoms'
import { toLineItem } from '../../common/adapters'
import { lineItemToFormState } from './adapters'
import { isFieldInvalid } from '../../common/validation'
import { secondaryOptionsVisibleAtom } from '../../common/atoms'
import { servicePeriodStringToDates } from '../../common/utils/servicePeriodStringToDates'

export const EditLineItem = (props: EditLineItemConfig & BaseConfig) => {
  const [lineItemGroups, setLineItemGroups] = useAtom(lineItemGroupsAtom)

  const lineItem = lineItemGroups
    ?.find(group => group.lineItems.some(item => item.id === props.lineItemId))
    ?.lineItems.find(item => item.id === props.lineItemId)

  invariant(lineItem, 'Line item not found')

  useHydrateAtoms([[formStateAtom, lineItemToFormState(lineItem)]])

  const [title, setTitle] = useAtom(titleAtom)
  const [quantity, setQuantity] = useAtom(quantityAtom)
  const [rate, setRate] = useAtom(rateAtom)
  const [rateDisplay, setRateDisplay] = useAtom(rateDisplayAtom)
  const [descriptionEnabled, setDescriptionEnabled] = useAtom(
    descriptionEnabledAtom
  )
  const [description, setDescription] = useAtom(descriptionAtom)
  const [externalIds, setExternalIds] = useAtom(externalIdsAtom)

  const validationResults = useAtomValue(validationResultsAtom)
  const validateForm = useSetAtom(validateFormAtom)

  const [secondaryOptionsVisible, setSecondaryOptionsVisible] = useAtom(
    secondaryOptionsVisibleAtom
  )

  const onUpdate = useCallback(() => {
    const result = validateForm()

    if (!result || !lineItemGroups) return

    setLineItemGroups(
      lineItemGroups.map(group => {
        if (group.id !== lineItem.groupId) return group

        return {
          ...group,
          lineItems: group.lineItems.map(item => {
            if (item.id !== props.lineItemId) return item

            return toLineItem({
              id: item.id,
              title,
              quantity,
              rate,
              rateDisplay,
              descriptionEnabled,
              description,
              taxCategoryId: group.taxCategory?.id ?? '',
              externalIds,
              servicePeriod: item.servicePeriod
                ? servicePeriodStringToDates(item.servicePeriod)
                : undefined,
              revenueRecognitionMethod: item.revenueRecognitionMethod,
              revenueClassification: item.revenueClassification,
              productId: item.productId,
              priceId: item.priceId,
              creditGrantId: item.creditGrantId
            })
          })
        }
      })
    )

    props.onDismiss()
  }, [
    description,
    descriptionEnabled,
    lineItem.groupId,
    lineItemGroups,
    props,
    quantity,
    rate,
    rateDisplay,
    title,
    externalIds,
    validateForm,
    setLineItemGroups
  ])

  return (
    <>
      <Title>Edit sub item</Title>

      <FieldGrid>
        <GridItem gridColumn="span 2">
          <FieldLabel label="Name" validationErrors={validationResults?.title}>
            <TextFieldInput
              placeholder="Name"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
              isInvalid={isFieldInvalid(validationResults?.title)}
              data-1p-ignore
              data-lpignore
            />
          </FieldLabel>
        </GridItem>
        <GridItem>
          <FieldLabel label="Qty" textAlign="right">
            <QuantityField
              value={quantity}
              onChange={setQuantity}
              rateDisplay={rateDisplay}
              isInvalid={isFieldInvalid(validationResults?.quantity)}
            />
          </FieldLabel>
        </GridItem>
        <GridItem>
          <FieldLabel label="Rate" textAlign="right">
            <RateField
              value={rate}
              onChange={setRate}
              rateDisplay={rateDisplay}
              isInvalid={isFieldInvalid(validationResults?.rate)}
            />
          </FieldLabel>
        </GridItem>
      </FieldGrid>

      <SecondaryOptions
        isVisible={secondaryOptionsVisible}
        setIsVisible={setSecondaryOptionsVisible}
        toggles={[
          <PercentageRatesToggle
            key="percentage-rates-toggle"
            rateDisplay={rateDisplay}
            setRateDisplay={setRateDisplay}
          />
        ]}
      >
        <SecondaryOptions.Group>
          <SecondaryOptions.ToggleField
            label="Description"
            isVisible={descriptionEnabled}
            setIsVisible={setDescriptionEnabled}
          >
            <DescriptionField
              description={description}
              setDescription={setDescription}
              validationErrors={validationResults?.description}
            />
          </SecondaryOptions.ToggleField>
        </SecondaryOptions.Group>

        <SecondaryOptions.Group>
          <LedgerIntegrationFields
            customerLedgerIntegrations={props.customerLedgerIntegrations}
            externalIds={externalIds}
            setExternalIds={setExternalIds}
            validationResults={validationResults?.externalIds ?? []}
          />
        </SecondaryOptions.Group>
      </SecondaryOptions>

      <Controls>
        <Controls.Cancel onClick={props.onDismiss} />
        <Controls.Confirm
          onClick={onUpdate}
          label="Update"
          keyboardShortcut={['Mod', 'Enter']}
        />
      </Controls>
    </>
  )
}
